import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
  FaFileExcel,
} from "react-icons/fa";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/lubeStockPositionRpt";

const toStr = (v, d = "") => (v === null || v === undefined ? d : String(v)).trim();
const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const LubeStockPositionReport = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [showFilters, setShowFilters] = useState(true);

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Meta
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const [branches, setBranches] = useState([{ label: "Select", value: "Select" }]);
  const [months, setMonths] = useState([{ label: "Select", value: "Select" }]);
  const [years, setYears] = useState([{ label: "Select", value: "Select" }]);
  const [itemNames, setItemNames] = useState([{ label: "Select", value: "Select" }]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [locks, setLocks] = useState({ branchLocked: false, branchValue: "" });

  const [itemCategory, setItemCategory] = useState({
    id: "",
    name: "FUELS AND LUBRICANTS",
    locked: true,
  });

  const [filters, setFilters] = useState({
    branch: "Select",
    month: "Select",
    year: "Select",
    itemCategoryId: "",
    itemName: "Select",
  });

  // Data
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);

  const totalPages = useMemo(
    () => Math.ceil((rows?.length || 0) / itemsPerPage),
    [rows, itemsPerPage]
  );

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return rows.slice(start, start + itemsPerPage);
  }, [rows, currentPage, itemsPerPage]);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const buildPrintQuery = (payload = {}) => {
    const qs = new URLSearchParams();
    Object.entries(payload).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      const s = String(v).trim();
      if (!s) return;
      qs.set(k, s);
    });
    return qs.toString();
  };

  /* ---------------- API: INIT ---------------- */
  const fetchInit = async () => {
    setLoadingInit(true);
    try {
      const res = await axios.get(`${API_BASE}/init`, {
        params: {
          branchType,
          branchId,
        },
        headers: commonHeaders,
      });

      if (!res.data?.success) throw new Error(res.data?.message || "Init failed");

      const data = res.data;

      setBranches(data.branches || [{ label: "Select", value: "Select" }]);
      setMonths(data.months || [{ label: "Select", value: "Select" }]);
      setYears(data.years || [{ label: "Select", value: "Select" }]);
      setItemNames(data.itemNames || [{ label: "Select", value: "Select" }]);

      setSuppliers(data.suppliers || []);
      setSelectedSuppliers([]);

      setLocks(data.userLock || { branchLocked: false, branchValue: "" });

      setItemCategory({
        id: toStr(data.itemCategory?.id || ""),
        name: toStr(data.itemCategory?.name || "FUELS AND LUBRICANTS"),
        locked: !!data.itemCategory?.locked,
      });

      setFilters((p) => ({
        ...p,
        branch: data.userLock?.branchLocked
          ? (data.userLock?.branchValue || "Select")
          : p.branch,
        itemCategoryId: toStr(data.itemCategory?.id || p.itemCategoryId),
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load dropdowns");
    } finally {
      setLoadingInit(false);
    }
  };

  useEffect(() => {
    fetchInit();
  }, []);

  /* ---------------- API: LIST ---------------- */
  const fetchList = async (mode = "search") => {
    if (mode === "search") {
      if (isSelect(filters.month)) return toast.error("Please select Month");
      if (isSelect(filters.year)) return toast.error("Please select Year");
    }

    if (isSelect(filters.branch)) return toast.error("Please select Branch");
    if (isSelect(filters.month)) return toast.error("Please select Month");
    if (isSelect(filters.year)) return toast.error("Please select Year");

    setLoadingList(true);
    try {
      const suppliersCsv =
        selectedSuppliers.length > 0 ? `${selectedSuppliers.join(",")},` : "";

      const res = await axios.get(`${API_BASE}/list`, {
        params: {
          branchId: filters.branch,
          month: filters.month,
          year: filters.year,
          itemCategoryId: filters.itemCategoryId || itemCategory.id,
          itemId: filters.itemName || "Select",
          suppliersCsv,
          date: "",
        },
        headers: commonHeaders,
      });

      if (!res.data?.success) throw new Error(res.data?.message || "List failed");

      setRows(res.data.rows || []);
      setColumns(res.data.columns || []);
      setCurrentPage(1);

      toast.success(`Found ${(res.data.rows || []).length} records`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to fetch report");
      setRows([]);
      setColumns([]);
    } finally {
      setLoadingList(false);
    }
  };

  /* ---------------- handlers ---------------- */
  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearch = () => fetchList("search");

  const handleViewAll = () => {
    toast.info("Showing records based on selected Month/Year");
    fetchList("all");
  };

  const handleClearFilters = () => {
    setSelectedSuppliers([]);
    setFilters((p) => ({
      ...p,
      branch: locks.branchLocked ? (locks.branchValue || "Select") : "Select",
      month: "Select",
      year: "Select",
      itemCategoryId: itemCategory.id || p.itemCategoryId,
      itemName: "Select",
    }));
    toast.info("Filters cleared!");
  };

  const handlePrint = () => {
    if (isSelect(filters.branch)) return toast.error("Please select Branch");
    if (isSelect(filters.month)) return toast.error("Please select Month");
    if (isSelect(filters.year)) return toast.error("Please select Year");

    const suppliersCsv =
      selectedSuppliers.length > 0 ? `${selectedSuppliers.join(",")},` : "";

    const payload = {
      branchId: filters.branch,
      month: filters.month,
      year: filters.year,
      itemcategoryid: filters.itemCategoryId || itemCategory.id,
      itemname: filters.itemName || "Select",
      supplier: suppliersCsv || "%",
      date: "",
    };

    const q = buildPrintQuery(payload);
    navigate(`/vehicle/LubeStockPositionReport_Print?${q}`);
  };

  const handleExcelExport = async () => {
    toast.info("Excel export not connected (add /export-xlsx if needed)");
  };

  /* ---------------- dynamic table render ---------------- */
  const renderTable = () => {
    if (!rows?.length) return null;

    const cols = ["S.No", ...(columns || [])];

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={`${buttonClass} text-white`}>
            <tr>
              {cols.map((c) => (
                <th key={c} className="px-4 py-4 text-center font-semibold whitespace-nowrap">
                  {c}
                </th>
              ))}
              <th className="px-4 py-4 text-center font-semibold">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {pagedRows.map((r, idx) => (
              <tr
                key={idx}
                className={`${
                  isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-50"
                } transition-colors duration-200`}
              >
                <td
                  className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                    isDark ? "text-gray-300" : "text-gray-900"
                  }`}
                >
                  {(currentPage - 1) * itemsPerPage + idx + 1}
                </td>

                {(columns || []).map((c) => (
                  <td
                    key={c}
                    className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                      isDark ? "text-gray-300" : "text-gray-900"
                    }`}
                    title={toStr(r[c], "")}
                  >
                    {toStr(r[c], "")}
                  </td>
                ))}

                <td className="px-4 py-4 text-center">
                  <button
                    onClick={handlePrint}
                    className={`p-2 rounded ${
                      isDark
                        ? "bg-gray-700 text-white hover:bg-gray-600"
                        : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                    }`}
                    title="Print"
                  >
                    <FaPrint className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">LUBE STOCK POSITION REPORT</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white text-black`
        }`}
      >
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Lube Stock Position Report
            </h1>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
              }`}
            >
              {rows.length} Records
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                  : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              onClick={handleExcelExport}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaFileExcel className="w-4 h-4" />
              Excel
            </button>

            <button
              onClick={handlePrint}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
              title="Print"
            >
              <FaPrint className="w-4 h-4" />
              Print
            </button>

            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </div>

        {showFilters && (
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              <FaFilter /> FILTRATION AREA
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              <div>
                <label className={labelClass}>Branch</label>
                <select
                  name="branch"
                  value={filters.branch}
                  onChange={handleChange}
                  className={inputClass}
                  disabled={locks.branchLocked}
                >
                  {(branches || []).map((b) => (
                    <option key={toStr(b.value)} value={toStr(b.value)}>
                      {toStr(b.label)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Month</label>
                <select name="month" value={filters.month} onChange={handleChange} className={inputClass}>
                  {(months || []).map((m) => (
                    <option key={toStr(m.value)} value={toStr(m.value)}>
                      {toStr(m.label)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Year</label>
                <select name="year" value={filters.year} onChange={handleChange} className={inputClass}>
                  {(years || []).map((y) => (
                    <option key={toStr(y.value)} value={toStr(y.value)}>
                      {toStr(y.label)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Item Category</label>
                <select className={inputClass} value={itemCategory.id || filters.itemCategoryId} disabled>
                  <option value={itemCategory.id || filters.itemCategoryId}>{itemCategory.name}</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Item Name</label>
                <select
                  name="itemName"
                  value={filters.itemName}
                  onChange={handleChange}
                  className={inputClass}
                >
                  {(itemNames || []).map((it) => (
                    <option key={toStr(it.value)} value={toStr(it.value)}>
                      {toStr(it.label)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2 lg:col-span-3">
                <label className={labelClass}>Suppliers (optional)</label>
                <div
                  className={`p-3 rounded-lg border ${
                    isDark ? "border-gray-600 bg-gray-700/20" : "border-gray-200 bg-white"
                  }`}
                >
                  {loadingInit ? (
                    <div className={isDark ? "text-gray-300" : "text-gray-600"}>Loading suppliers...</div>
                  ) : suppliers.length === 0 ? (
                    <div className={isDark ? "text-gray-300" : "text-gray-600"}>No suppliers</div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {suppliers.map((s) => {
                        const name = toStr(s.label);
                        const checked = selectedSuppliers.includes(name);
                        return (
                          <label key={toStr(s.value)} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                setSelectedSuppliers((prev) => {
                                  if (e.target.checked) return [...prev, name];
                                  return prev.filter((x) => x !== name);
                                });
                              }}
                            />
                            <span className={isDark ? "text-gray-200" : "text-gray-800"}>{name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleClearFilters}
                className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
              >
                Clear All Filters
              </button>

              <div className="flex gap-4">
                <button
                  onClick={handleViewAll}
                  disabled={loadingList}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60"
                >
                  <FaSearch />
                  View All
                </button>

                <button
                  onClick={handleSearch}
                  disabled={loadingList}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60`}
                >
                  <FaSearch />
                  {loadingList ? "Searching..." : "Search Records"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
          {loadingList ? (
            <div className="text-center py-16">
              <div className={isDark ? "text-gray-200" : "text-gray-700"}>Loading report...</div>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                  isDark ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                No Records Found
              </h3>
              <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Select Branch, Month and Year then click Search.
              </p>
            </div>
          ) : (
            renderTable()
          )}
        </div>

        {rows.length > 0 && (
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
            <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, rows.length)} of {rows.length} entries
            </div>

            <nav className="flex gap-1">
              <button
                onClick={() => paginate(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 border rounded text-sm ${
                  currentPage === 1
                    ? isDark
                      ? "border-gray-700 text-gray-500 cursor-not-allowed"
                      : "border-gray-300 text-gray-400 cursor-not-allowed"
                    : isDark
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                }`}
              >
                Previous
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;

                return (
                  <button
                    key={pageNum}
                    onClick={() => paginate(pageNum)}
                    className={`px-3 py-1 border rounded text-sm ${
                      currentPage === pageNum
                        ? `${buttonClass} text-white`
                        : isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 border rounded text-sm ${
                  currentPage === totalPages
                    ? isDark
                      ? "border-gray-700 text-gray-500 cursor-not-allowed"
                      : "border-gray-300 text-gray-400 cursor-not-allowed"
                    : isDark
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                }`}
              >
                Next
              </button>
            </nav>
          </div>
        )}

        <p className={`text-xs text-right mt-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          INFRA SCREEN : B-63
        </p>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
};

export default LubeStockPositionReport;